import { useCallback, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import * as turf from '@turf/turf';
import { Units, Position } from '@turf/turf';
import { Marker, GeolocateControl, Source, Layer, ViewStateChangeEvent } from 'react-map-gl/maplibre';
import { useTranslation } from 'next-i18next';

import { getAssetSrc, cn } from '@restworld/utils-common';

import CurrentLocationPinControlButton from './location-pin-control-button';
import { mapDefaultArgsType } from './job-location-map.mocks';
import { MapWrapper } from '../../design-system';
import { Button } from '../../';

const radius = 0.5;

export const MapComponent: React.FunctionComponent<IMapProps> = ({
  lon,
  lat,
  approximateLat,
  approximateLon,
  showRestaurant,
}) => {
  const mapRef = useRef<MapRef>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Map loads are free since the MapLibre/OpenFreeMap migration, so the click-to-load
  // gate is gone: the map mounts on its own as soon as it scrolls near the viewport
  // (not at page load, to keep LCP of the SEO job pages unaffected).
  const [toggleMapView, mapViewToggler] = useState(false);
  React.useEffect(() => {
    const node = wrapperRef.current;
    if (!node || toggleMapView) return;
    if (typeof IntersectionObserver === 'undefined') {
      mapViewToggler(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) mapViewToggler(true);
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [toggleMapView]);
  let coordinates: number[] = useMemo(
    () => (showRestaurant ? [lon, lat] : [approximateLon, approximateLat]),
    [approximateLat, approximateLon, lat, lon, showRestaurant]
  );
  coordinates = coordinates.length ? [...coordinates].map((v) => (v ? Number(v) : 0)) : [];
  const options: { steps: number; units: Units } = { steps: 100, units: 'kilometers' };
  const circle = turf.circle(coordinates, radius, options);
  const cor: Position[] = circle.geometry?.coordinates[0];
  const line = turf.lineString(cor);
  const [zoomLevel, setZoomLevel] = React.useState<number>(13);
  const [showCurrentLocationPinCtlButton, setShowCurrentLocationPinCtlButton] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (zoomLevel < 13) {
      setShowCurrentLocationPinCtlButton(true);
    } else {
      setShowCurrentLocationPinCtlButton(false);
    }
  }, [zoomLevel]);

  const handleZoomEnd = useCallback((e: ViewStateChangeEvent) => {
    setZoomLevel(e.viewState.zoom);
  }, []);

  const handleClickCurrentLocationPinCtlButton = useCallback(() => {
    mapRef.current?.flyTo({ center: [coordinates[0], coordinates[1]], duration: 2000, zoom: 13 });
  }, [coordinates]);

  const radiusEl = useMemo(
    () => (
      <>
        <Source id="my-data" type="geojson" data={circle}>
          <Layer
            id="point-90-hi"
            type="fill"
            paint={{
              'fill-color': '#693eb5',
              'fill-opacity': 0.08,
            }}
          />
        </Source>
        <Source id="approx-circle" type="geojson" data={line}>
          <Layer
            id="point-9-hi"
            type="line"
            paint={{
              'line-color': '#693eb5',
              'line-width': 1,
            }}
          />
        </Source>
      </>
    ),
    [circle, line]
  );

  const marker = useMemo(
    () => (
      <Marker longitude={coordinates[0]} latitude={coordinates[1]}>
        <img src={getAssetSrc('/utils-assets/location-marker.svg')} alt="" />
      </Marker>
    ),
    [coordinates]
  );

  return (
    <Wrapper ref={wrapperRef}>
      {toggleMapView ? (
        <>
          {showCurrentLocationPinCtlButton ? (
            <CurrentLocationPinControlButton onClick={handleClickCurrentLocationPinCtlButton} />
          ) : (
            <div className="h-[29px]" />
          )}
          <MapWrapper
            coordinates={coordinates}
            mapRef={mapRef}
            handleZoomEnd={handleZoomEnd}
            // centering used to happen via a delayed flyTo in the click handler of the
            // removed placeholder button
            initialViewState={{ longitude: coordinates[0], latitude: coordinates[1], zoom: 13 }}
          >
            {/* Marker always visible when showDetails is true */}
            {/* Marker always visible when showDetails is true */}
            {showRestaurant || zoomLevel <= 9 ? (
              marker
            ) : zoomLevel >= 11.5 ? (
              radiusEl
            ) : (
              <>
                {marker}
                {radiusEl}
              </>
            )}
            <GeolocateControl positionOptions={{ enableHighAccuracy: true }} trackUserLocation={true} />
          </MapWrapper>
        </>
      ) : (
        <div className="absolute w-full h-full z-[8]">
          <img
            src={getAssetSrc('/utils-assets/map-placeholder-image.png')}
            className="w-full h-full object-cover"
            alt=""
          />
        </div>
      )}
    </Wrapper>
  );
};
export default MapComponent;
export type IMapProps = mapDefaultArgsType;
const Wrapper = ({ className, ...props }: any) => (
  <div className={cn('h-96 w-full mb-[36px] relative [&_.maplibregl-map]:rounded', className)} {...props} />
);
export const ImageLoadPlaceholder = ({ mapViewToggler }: { mapViewToggler: () => void }) => {
  const { t } = useTranslation('common');
  return (
    <div className="absolute w-full h-full z-[8]">
      <div className="relative w-full h-full inline-block">
        <img
          src={getAssetSrc('/utils-assets/map-placeholder-image.png')}
          className="w-full h-full object-cover"
          alt=""
        />
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <Button title={t('view_location_on_map')} variant="secondary-outline" onClick={mapViewToggler} />
        </span>
      </div>
    </div>
  );
};
export * from './job-location-map.mocks';
